// E37-t6 — THE CORPUS SNAPSHOT, as the browser may ask for it.
//
// Contract: git-host/src/corpus-read.ts — two GETs and nothing else:
//   GET /api/:org/:kb/files          the snapshot manifest: the checkout's
//                                    commit, branch, updatedAt and every file
//                                    (path, size, blob oid), verbatim from
//                                    corpus/{org}/{kb}.json (src/snapshot.ts).
//   GET /api/:org/:kb/files/raw?path=…  one file's raw bytes. The ETag is the
//                                    blob oid; this client reads TEXT, because
//                                    the one consumer (KbFiles.tsx) renders
//                                    markdown and shows nothing binary.
//
// The manifest is the WHOLE corpus — sidecars, formats, dashboards included —
// because the door answers "the vault as checked in" and hides nothing. What
// is SHOWN of it (documents, not data) is the page's decision, taken where the
// tree is drawn. Shaped like src/api/kbs.ts: late-bound fetch, refusals
// relayed in the server's own words.

import { appFetch } from './transport';
/** One row of the manifest's file list — git-host/src/snapshot.ts CorpusFile. */
export interface CorpusFile {
  /** repo-relative path, the same string the raw route is asked with */
  path: string;
  /** the blob's byte length — what decides the 500 KB reading cap up front */
  size: number;
  /** the blob's git oid — content identity, stable across unrelated pushes */
  oid: string;
}

/** GET /api/:org/:kb/files — the checkout, named by its commit. */
export interface CorpusTree {
  commit: string;
  branch: string;
  updatedAt: string;
  files: CorpusFile[];
}

/** The questions the file browser asks. Injected, so no page holds a fetch. */
export interface FilesClient {
  loadTree(org: string, kb: string): Promise<CorpusTree>;
  /** one file, as text — the raw route's body, byte for byte */
  loadFile(org: string, kb: string, path: string): Promise<string>;
}

export const filesEndpoint = (org: string, kb: string): string =>
  `/api/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/files`;

export const fileRawEndpoint = (org: string, kb: string, path: string): string =>
  `${filesEndpoint(org, kb)}/raw?path=${encodeURIComponent(path)}`;

const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** The server's own words, exactly as src/api/kbs.ts relays them. */
async function refusalOf(response: Response, what: string): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as {
    guard?: string;
    reason?: string;
    message?: string;
    error?: string;
  };
  const said = body.message ?? body.reason ?? body.error;
  return new Error(
    `${body.guard ? `${body.guard}: ` : ''}${said ?? `${what} answered ${response.status}`}`,
  );
}

export function createFilesClient(
  fetchImpl: typeof fetch = (input, init) => appFetch(input, init),
): FilesClient {
  return {
    async loadTree(org: string, kb: string): Promise<CorpusTree> {
      const path = filesEndpoint(org, kb);
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `GET ${path}`);
      const body = (await response.json()) as Record<string, unknown>;
      const files: CorpusFile[] = [];
      for (const entry of Array.isArray(body.files) ? body.files : []) {
        const raw = entry as Record<string, unknown>;
        const filePath = str(raw?.path);
        // a row without a path cannot be drawn or fetched — dropped rather
        // than rendered as a node that leads nowhere (src/api/kbs.ts's rule)
        if (!filePath) continue;
        files.push({ path: filePath, size: num(raw.size), oid: str(raw.oid) });
      }
      return {
        commit: str(body.commit),
        branch: str(body.branch),
        updatedAt: str(body.updatedAt),
        files,
      };
    },

    async loadFile(org: string, kb: string, path: string): Promise<string> {
      const url = fileRawEndpoint(org, kb, path);
      const response = await fetchImpl(url, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `GET ${url}`);
      return response.text();
    },
  };
}
